import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './EntityPicker.css';

/**
 * Modal for selecting existing entities or creating new ones.
 *
 * @param {boolean} open - Whether the picker is visible.
 * @param {Function} onClose - Called when the picker is dismissed.
 * @param {string} title - Modal heading, e.g. "Add Extraction Field".
 * @param {Array} allEntities - All available entities of this type.
 * @param {Array} excludeIds - IDs already in the current config (hidden from list).
 * @param {Function} getLabel - Returns display name for an entity.
 * @param {Function} getPreview - Returns preview text for an entity (optional).
 * @param {Function} onSelect - Called with the selected entity's ID.
 * @param {Function} onCreateNew - Called when "Create New" is clicked. If null, button hidden.
 * @param {Array} allConfigs - All facility configs (for computing "Used by" labels).
 * @param {Function} getConfigRefs - Given a config, returns array of entity IDs it references.
 */
export default function EntityPicker({
  open,
  onClose,
  title = 'Add Entity',
  allEntities = [],
  excludeIds = [],
  getLabel = (e) => e.name || e.id,
  getPreview = () => null,
  onSelect,
  onCreateNew,
  allConfigs = [],
  getConfigRefs = () => [],
}) {
  const [search, setSearch] = useState('');

  // Compute which configs reference each entity
  const usageMap = useMemo(() => {
    const map = {};
    allConfigs.forEach((config) => {
      const refs = getConfigRefs(config);
      refs.forEach((refId) => {
        if (!map[refId]) map[refId] = [];
        map[refId].push(config.name || config.id);
      });
    });
    return map;
  }, [allConfigs, getConfigRefs]);

  const available = useMemo(() => {
    const excluded = new Set(excludeIds);
    return allEntities
      .filter((e) => !excluded.has(e.id))
      .filter((e) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
          getLabel(e).toLowerCase().includes(q) ||
          (getPreview(e) || '').toLowerCase().includes(q)
        );
      });
  }, [allEntities, excludeIds, search, getLabel, getPreview]);

  if (!open) return null;

  return createPortal(
    <div className="ep-overlay" onClick={onClose}>
      <div className="ep-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="ep-header">
          <h3>{title}</h3>
          <button className="ep-close" onClick={onClose}>&times;</button>
        </div>

        <div className="ep-search">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className="ep-list">
          {available.length === 0 ? (
            <div className="ep-empty">
              {search.trim() ? 'No matches found.' : 'All entities are already added.'}
            </div>
          ) : (
            available.map((entity) => {
              const preview = getPreview(entity);
              const usedBy = usageMap[entity.id] || [];
              return (
                <div key={entity.id} className="ep-item" onClick={() => { onSelect(entity.id); onClose(); }}>
                  <div className="ep-item-content">
                    <div className="ep-item-name">{getLabel(entity)}</div>
                    {preview && <div className="ep-item-preview">{preview}</div>}
                    {usedBy.length > 0 && (
                      <div className="ep-item-usage">
                        Used by: {usedBy.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="ep-item-add">+</div>
                </div>
              );
            })
          )}
        </div>

        {onCreateNew && (
          <div className="ep-footer">
            <button className="ep-create-btn" onClick={() => { onCreateNew(); onClose(); }}>
              + Create New
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
